/**
 * useReferralCapture
 *
 * On any page mount:
 *   1. Parse `?ref=CODE` from the current URL. If present, persist it in
 *      localStorage under a namespaced key with a 30-day expiry.
 *   2. If the URL had a fresh `?ref=`, fire a `link_click` funnel event
 *      (best-effort, silent-fail).
 *   3. Return `{ refCode, trackEvent }` so the caller can attribute
 *      downstream funnel steps (tour_view, tour_started, tour_purchased).
 *
 * Design notes:
 *   - The stored code survives page refreshes AND tab restarts (localStorage).
 *   - Overwriting with a newer `?ref=` is intentional — a fresher click wins
 *     attribution. This matches Stripe/Amazon behaviour.
 *   - We DO NOT block the UI on the tracking POST. All calls are fire-and-
 *     forget with try/catch.
 *   - No PII is sent. Only ref_code + session_id + event metadata.
 */
import { useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'audiostride_ref';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.code) return null;
    if (parsed.expires_at && parsed.expires_at < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch (_) {
    return null;
  }
};

const writeStored = (code) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code, saved_at: Date.now(), expires_at: Date.now() + TTL_MS }),
    );
  } catch (_) { /* quota errors — ignore */ }
};

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem('audiostride_visitor_session');
    if (!sid) {
      sid = `vis-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('audiostride_visitor_session', sid);
    }
    return sid;
  } catch (_) {
    return `vis-${Date.now()}`;
  }
};

/**
 * @param {string} apiBase — the API base URL (`${BACKEND_URL}/api`)
 */
export function useReferralCapture(apiBase) {
  useEffect(() => {
    if (!apiBase) return;
    try {
      const url = new URL(window.location.href);
      const incoming = (url.searchParams.get('ref') || '').trim().toLowerCase();
      if (!incoming) return;

      writeStored(incoming);

      // Fire link_click funnel event once per URL visit — de-dupe within a
      // 5-minute window per ref+session to survive React double-mounts in
      // dev + rapid back/forward.
      const dedupeKey = `audiostride_ref_click:${incoming}:${getSessionId()}`;
      let last = 0;
      try { last = parseInt(sessionStorage.getItem(dedupeKey) || '0', 10); } catch (_) { /* noop */ }
      if (Date.now() - last < 5 * 60 * 1000) return;
      try { sessionStorage.setItem(dedupeKey, String(Date.now())); } catch (_) { /* noop */ }

      axios.post(`${apiBase}/referrals/track-event`, {
        event_type: 'link_click',
        ref_code: incoming,
        session_id: getSessionId(),
        metadata: { landing_url: window.location.pathname },
      }).catch(() => { /* silent — attribution is best-effort */ });
    } catch (_) { /* URL parsing errors — silent */ }
  }, [apiBase]);

  const refCode = useMemo(() => readStored(), []);

  const trackEvent = useCallback((eventType, extra = {}) => {
    if (!apiBase) return;
    const stored = readStored();
    if (!stored) return; // no attribution — skip
    try {
      axios.post(`${apiBase}/referrals/track-event`, {
        event_type: eventType,
        ref_code: stored,
        session_id: getSessionId(),
        ...extra,
      }).catch(() => {});
    } catch (_) { /* noop */ }
  }, [apiBase]);

  return { refCode, trackEvent };
}
